import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import Dropdown from 'react-bootstrap/Dropdown';
import { BsArrowsMove, BsPencilSquare, BsTrash, BsPlusLg } from 'react-icons/bs';
import { createModule } from './utils/setModel';

const ModuleManager = ({
    selectedModules = [],
    availableModules = {},
    onModulesChange,
    onRequestEdit, // Function passed from SetTest
    isLoadingDefinitions = false
}) => {
    const addableModules = Object.entries(availableModules).filter(([, moduleDef]) => moduleDef?.active !== false);

    const handleAddModule = (moduleKey) => {
        const moduleDefinition = availableModules[moduleKey];
        if (!moduleDefinition || moduleDefinition.active === false) return;
        const newModule = createModule(moduleKey, moduleDefinition);
        onModulesChange([...selectedModules, newModule]);
    };

    const handleDeleteModule = (index) => {
        const moduleToDelete = selectedModules[index];
        const moduleName = availableModules[moduleToDelete?.type]?.name || 'this module';
        if (window.confirm(`Are you sure you want to delete "${moduleName}"?`)) {
            const newModules = selectedModules.filter((_, i) => i !== index);
            onModulesChange(newModules);
        }
    };

    const onDragEndModules = (result) => {
        const { source, destination } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }
        const items = Array.from(selectedModules);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        onModulesChange(items);
    };

    if (isLoadingDefinitions) return <p>Loading module definitions...</p>;

    return (
        <div className="set-module-manager">
            <div className="set-module-manager-summary">
                <div className="text-black"><strong>Page sections</strong><small>Drag sections to change their order.</small></div>
                <span>{selectedModules.length}</span>
            </div>
            <Dropdown className="mb-3">
                <Dropdown.Toggle variant="primary" id="dropdown-add-module" className="w-100 set-add-module-button">
                    <BsPlusLg /> Add page section
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 set-module-options">
                    {addableModules.length > 0 ? addableModules.map(([key, moduleDef]) => (
                        <Dropdown.Item key={key} onClick={() => handleAddModule(key)}>
                            <strong>{moduleDef.name}</strong><small>{moduleDef.description || 'Add this section to the page.'}</small>
                        </Dropdown.Item>
                    )) : <Dropdown.Item disabled>No modules available</Dropdown.Item>}
                </Dropdown.Menu>
            </Dropdown>

            <DragDropContext onDragEnd={onDragEndModules}>
                <Droppable droppableId="selectedModulesDroppable">
                    {(provided) => (
                        <ListGroup {...provided.droppableProps} ref={provided.innerRef} className="set-module-list">
                            {selectedModules.map((module, index) => (
                                <Draggable key={module.id} draggableId={module.id} index={index}>
                                    {(providedDraggable) => (
                                        <ListGroup.Item
                                            ref={providedDraggable.innerRef}
                                            {...providedDraggable.draggableProps}
                                            className="set-module-manager-item"
                                            style={{ ...providedDraggable.draggableProps.style }}
                                        >
                                            <div {...providedDraggable.dragHandleProps} className="set-module-manager-drag" title="Drag to reorder section">
                                                <BsArrowsMove />
                                            </div>
                                            <span className="set-module-manager-number">{index + 1}</span>
                                            <button type="button" className="set-module-manager-main" onClick={() => onRequestEdit(index)}>
                                                <strong>{availableModules[module.type]?.name || 'Unknown section'}</strong>
                                                <small>{module.features?.length ? `${module.features.length} feature${module.features.length === 1 ? '' : 's'}` : availableModules[module.type]?.description || 'Click to edit'}</small>
                                            </button>
                                            <div className="set-module-manager-actions">
                                                <Button
                                                    variant="light" size="sm"
                                                    onClick={() => onRequestEdit(index)} title="Edit section"
                                                    disabled={!onRequestEdit}
                                                >
                                                    <BsPencilSquare />
                                                </Button>
                                                <Button variant="light" size="sm" onClick={() => handleDeleteModule(index)} title="Delete section" className="text-danger">
                                                    <BsTrash />
                                                </Button>
                                            </div>
                                        </ListGroup.Item>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </ListGroup>
                    )}
                </Droppable>
            </DragDropContext>

            {selectedModules.length === 0 && <div className="set-module-manager-empty"><BsPlusLg /><strong>Your page is empty</strong><small>Add an introduction, card showcase, FAQ, or another section above.</small></div>}
        </div>
    );
};

export default ModuleManager;
